import React, { useContext } from 'react';
import { Grid, Button, Typography, IconButton, Tooltip, Stack } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import AuthContext from '../../../../context/authContext';
import useTicket from '../../../../hooks/useTicket';
import LockIconWrapper from '../../../components/LockIconWrapper';
import { isFeatureEnabled } from '../../../../config/featureFlags';

function FooterButtons({
  booking,
  hasOverrideCapacityRights = false,
  isEditable = false,
  onOpenCapacityOverrideDialog,
  onCommunicationHistoryOpen,
  onInvoiceModalOpen,
  onOnSiteProtocolModalOpen,
  onPaymentHistoryModalOpen,
  onBookingHistoryModalOpen,
  onExternalInvoiceCreate,
  externalInvoice,
  onExternalInvoiceView,
  onExternalInvoiceDownload,
}) {
  const authCtx = useContext(AuthContext);
  const { getTicketPDF } = useTicket();
  const canDownloadTicket =
    isFeatureEnabled('qrTickets') &&
    booking?.status === 'confirmed' &&
    authCtx.checkRightAny('ticket:read');

  const handleTicketDownload = async () => {
    try {
      const response = await getTicketPDF(booking._id);
      if (response) {
        const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Ticket_${booking.bookingNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      toast.error('Fehler beim Herunterladen des Tickets.');
    }
  };

  const hasCapacityOverride =
    (typeof booking?.capacityParticipantCount === 'number' &&
      booking.capacityParticipantCount >= 0) ||
    (typeof booking?.capacityLaneCount === 'number' && booking.capacityLaneCount >= 0);

  const showCapacityOverrideButton =
    hasOverrideCapacityRights && isEditable && !hasCapacityOverride;

  const isExternallyManaged = Boolean(booking?.entertainmentId?.isExternallyManaged);

  return (
    <>
      {booking.status !== 'blocked' && (
        <Grid container spacing={1} mt={2}>
          <Grid item xs={12} sm={6} lg={4}>
            <Button variant='outlined' size='small' onClick={onCommunicationHistoryOpen} fullWidth>
              Kommunikationsverlauf
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Button variant='outlined' size='small' onClick={onBookingHistoryModalOpen} fullWidth>
              Änderungsverlauf
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <Button variant='outlined' size='small' onClick={onOnSiteProtocolModalOpen} fullWidth>
              Aktivitätsverlauf
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} lg={showCapacityOverrideButton ? 4 : 6}>
            <Button variant='outlined' size='small' onClick={onPaymentHistoryModalOpen} fullWidth>
              Online-Zahlungen
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            sm={showCapacityOverrideButton ? 6 : 12}
            lg={showCapacityOverrideButton ? 4 : 6}
          >
            <Button variant='outlined' size='small' onClick={onInvoiceModalOpen} fullWidth>
              Rechnungen
            </Button>
          </Grid>
          {showCapacityOverrideButton && (
            <Grid item xs={12} sm={6} lg={4}>
              <Button
                variant='outlined'
                size='small'
                onClick={onOpenCapacityOverrideDialog}
                fullWidth
              >
                Kapazitätsübersteuerung hinzufügen
              </Button>
            </Grid>
          )}
          {canDownloadTicket && (
            <Grid item xs={12}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<ConfirmationNumberIcon />}
                onClick={handleTicketDownload}
                fullWidth
              >
                <LockIconWrapper right='ticket:read'>Ticket herunterladen</LockIconWrapper>
              </Button>
            </Grid>
          )}
          {isExternallyManaged && !externalInvoice && onExternalInvoiceCreate && (
            <Grid item xs={12}>
              <Button
                variant='outlined'
                size='small'
                onClick={onExternalInvoiceCreate}
                fullWidth
              >
                Rechnung erstellen
              </Button>
            </Grid>
          )}
          {isExternallyManaged && externalInvoice && (
            <Grid item xs={12}>
              <Stack direction='row' spacing={1}>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<VisibilityIcon />}
                  onClick={onExternalInvoiceView}
                  fullWidth
                >
                  Rechnung anzeigen
                </Button>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<DownloadIcon />}
                  onClick={onExternalInvoiceDownload}
                  fullWidth
                >
                  Rechnung herunterladen
                </Button>
              </Stack>
            </Grid>
          )}
        </Grid>
      )}
      {booking.meta && (
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{
            mt: 1,
            display: 'block',
            fontSize: '0.75rem',
            color: 'grey.500',
          }}
        >
          {/* The readable name when a user account is behind the entry, the raw value
              otherwise - some are system labels like "Public Self-Service". */}
          Erstellt am {dayjs(booking.meta.createdAt).format('DD.MM.YYYY HH:mm')} von{' '}
          {booking.meta.createdByName || booking.meta.createdBy}
          {booking.meta.editedAt && booking.meta.editedBy && (
            <>
              <br />
              Zuletzt bearbeitet am {dayjs(booking.meta.editedAt).format(
                'DD.MM.YYYY HH:mm'
              )} von {booking.meta.editedByName || booking.meta.editedBy}
            </>
          )}
        </Typography>
      )}
    </>
  );
}

export default FooterButtons;
